import React, { useState, useEffect, useCallback } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  GlobalStyles,
  Paper,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import StorageIcon from "@mui/icons-material/Storage";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { activityApi, ACTIVITY_TYPE_NEW_USER_GIFT } from "@/api";

const ACTIVITY_TYPE = ACTIVITY_TYPE_NEW_USER_GIFT;
const GIFT_SIZE_LABEL = "200MB";
const DEFAULT_SIZE_LABEL = "56MB";
const TOTAL_SIZE_LABEL = "256MB";

const colors = {
  red: "#c8102e",
  vermilion: "#f36b2c",
  gold: "#ffd166",
  deepGold: "#ffdf70",
  ivory: "#fff8e7",
  goldGlow: "rgba(255, 209, 102, 0.48)",
  darkText: "#8a1538",
};

const pageShellSx = {
  minHeight: "100vh",
  position: "relative",
  overflow: "hidden",
  background: `
    radial-gradient(circle at 50% 14%, rgba(255, 230, 156, 0.52) 0%, rgba(255, 209, 102, 0.2) 24%, transparent 52%),
    radial-gradient(circle at 18% 26%, rgba(255, 215, 120, 0.28) 0%, transparent 30%),
    radial-gradient(circle at 82% 24%, rgba(255, 215, 120, 0.26) 0%, transparent 26%),
    linear-gradient(180deg, #b30f2a 0%, #d91f3f 34%, #ef5a2f 70%, #d79b2d 100%)
  `,
  color: colors.ivory,
};

const festiveCardSx = {
  position: "relative",
  overflow: "hidden",
  background:
    "linear-gradient(180deg, rgba(207, 29, 58, 0.94) 0%, rgba(166, 18, 45, 0.92) 100%)",
  borderRadius: 5,
  border: "1px solid rgba(255, 225, 141, 0.54)",
  boxShadow: "0 28px 70px rgba(137, 28, 20, 0.24)",
  backdropFilter: "blur(8px)",
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    background:
      "linear-gradient(135deg, rgba(255, 239, 187, 0.24), transparent 24%, transparent 76%, rgba(255, 209, 102, 0.22))",
    pointerEvents: "none",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    inset: 12,
    borderRadius: 4,
    border: "1px solid rgba(255, 248, 231, 0.18)",
    pointerEvents: "none",
  },
};

const gradientBtnSx = {
  background: `linear-gradient(135deg, #ffe49a 0%, ${colors.gold} 40%, ${colors.deepGold} 100%)`,
  color: colors.darkText,
  fontWeight: 800,
  letterSpacing: "0.04em",
  boxShadow:
    "0 14px 30px rgba(172, 88, 18, 0.28), inset 0 1px 0 rgba(255, 255, 255, 0.45)",
  animation: "festivePulse 2.4s ease-in-out infinite",
  "&:hover": {
    background: `linear-gradient(135deg, #fff0b8 0%, ${colors.gold} 48%, #ffe16a 100%)`,
    boxShadow:
      "0 18px 36px rgba(172, 88, 18, 0.3), 0 0 30px rgba(255, 223, 112, 0.5)",
    transform: "translateY(-2px)",
  },
  "&.Mui-disabled": {
    color: "rgba(138, 21, 56, 0.68)",
    background:
      "linear-gradient(135deg, rgba(255, 228, 154, 0.72), rgba(255, 209, 102, 0.68))",
  },
  transition:
    "transform 0.25s ease, box-shadow 0.25s ease, background 0.25s ease",
};

function useToken() {
  const params = new URLSearchParams(window.location.search);
  return params.get("token") || "";
}

function ActivityPage() {
  const token = useToken();

  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("");
  const [claimedAt, setClaimedAt] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [claimError, setClaimError] = useState("");

  useEffect(() => {
    document.title = "新用户专属福利 - 知嘛";
  }, []);

  const fetchStatus = useCallback(async () => {
    if (!token) {
      setStatus("no_token");
      return;
    }

    setStatus("loading");
    setClaimError("");
    try {
      const payload = await activityApi.getStatus(ACTIVITY_TYPE, token);
      if (payload?.status === 1) {
        setStatus("claimed");
        setMessage(payload.message || "");
        setClaimedAt(payload.claimed_at || null);
      } else if (payload?.eligible) {
        setStatus("eligible");
        setMessage(payload.message || "");
        setClaimedAt(null);
      } else {
        setStatus("ineligible");
        setMessage(payload?.message || "您暂不符合领取条件");
        setClaimedAt(payload?.claimed_at || null);
      }
    } catch (error) {
      setStatus("error");
      setMessage(error.message || "网络异常，请检查网络连接后重试");
    }
  }, [token]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleClaim = async () => {
    setClaimError("");
    setClaiming(true);
    try {
      const { data: payload, message: responseMessage } = await activityApi.claim(
        ACTIVITY_TYPE,
        token,
      );
      setStatus("claimed");
      setMessage(responseMessage || payload?.message || "领取成功！");
      setClaimedAt(payload?.claimed_at || new Date().toISOString());
    } catch (error) {
      setClaimError(error.message || "网络异常，请稍后重试");
    } finally {
      setClaiming(false);
    }
  };

  if (status === "loading") {
    return (
      <PageShell>
        <CircularProgress size={48} sx={{ color: colors.gold }} />
        <Typography sx={{ mt: 2, color: "rgba(255, 248, 231, 0.82)" }}>
          正在为您点亮活动礼遇…
        </Typography>
      </PageShell>
    );
  }

  if (status === "no_token") {
    return (
      <PageShell>
        <StatusCard
          icon={
            <ErrorOutlineIcon
              sx={{ fontSize: 56, color: colors.gold, mb: 2 }}
            />
          }
          title="链接无效"
        >
          <Typography sx={{ color: "rgba(255, 248, 231, 0.86)" }}>
            请通过知嘛小程序内的活动入口访问本页面
          </Typography>
        </StatusCard>
      </PageShell>
    );
  }

  if (status === "error") {
    return (
      <PageShell>
        <StatusCard
          icon={
            <ErrorOutlineIcon
              sx={{ fontSize: 56, color: colors.gold, mb: 2 }}
            />
          }
          title="加载失败"
        >
          <Typography sx={{ color: "rgba(255, 248, 231, 0.82)", mb: 3 }}>
            {message}
          </Typography>
          <Button variant="contained" onClick={fetchStatus} sx={gradientBtnSx}>
            重新加载
          </Button>
        </StatusCard>
      </PageShell>
    );
  }

  if (status === "claimed") {
    return (
      <PageShell>
        <SuccessCard claimedAt={claimedAt} message={message} />
      </PageShell>
    );
  }

  if (status === "ineligible") {
    return (
      <PageShell>
        <StatusCard
          icon={
            <ErrorOutlineIcon
              sx={{ fontSize: 56, color: colors.gold, mb: 2 }}
            />
          }
          title="暂不符合条件"
        >
          <Typography sx={{ color: "rgba(255, 248, 231, 0.82)" }}>
            {message}
          </Typography>
        </StatusCard>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <Container
        maxWidth="sm"
        sx={{
          position: "relative",
          zIndex: 2,
          py: { xs: 5, sm: 6 },
          pb: { xs: 16, sm: 18 },
        }}
      >
        <Paper elevation={0} sx={{ ...festiveCardSx, p: { xs: 3, sm: 4 } }}>
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: 76,
              height: 76,
              borderTop: `3px solid ${colors.deepGold}`,
              borderLeft: `3px solid ${colors.deepGold}`,
              borderTopLeftRadius: 20,
              opacity: 0.9,
            }}
          />
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              width: 76,
              height: 76,
              borderTop: `3px solid ${colors.deepGold}`,
              borderRight: `3px solid ${colors.deepGold}`,
              borderTopRightRadius: 20,
              opacity: 0.9,
            }}
          />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: { xs: 1.5, sm: 2.5 },
              mb: 4,
              flexWrap: "wrap",
            }}
          >
            <StorageBlock
              label="开通前"
              value={DEFAULT_SIZE_LABEL}
              color={colors.ivory}
              bgColor="rgba(255, 248, 231, 0.08)"
            />

            <Box textAlign="center" sx={{ px: 0.5 }}>
              <Typography
                sx={{
                  color: colors.gold,
                  fontSize: { xs: "1.35rem", sm: "1.6rem" },
                  fontWeight: 900,
                  lineHeight: 1,
                  textShadow: "0 0 18px rgba(255, 209, 102, 0.35)",
                }}
              >
                +{GIFT_SIZE_LABEL}
              </Typography>
              <Typography
                sx={{ fontSize: "1.45rem", mt: 0.5, color: colors.ivory }}
              >
                →
              </Typography>
            </Box>

            <StorageBlock
              label="领取后，能下载50+优质文档"
              value={TOTAL_SIZE_LABEL}
              color={colors.gold}
              bgColor="rgba(255, 209, 102, 0.12)"
              glow
            />
          </Box>

          <Box
            sx={{
              background:
                "linear-gradient(135deg, rgba(255, 238, 186, 0.18), rgba(255, 209, 102, 0.1))",
              border: "1px solid rgba(255, 233, 156, 0.32)",
              borderRadius: 3,
              px: 2,
              py: 1.4,
              mb: 3,
            }}
          >
            <Typography
              sx={{ color: colors.gold, fontWeight: 700, textAlign: "center" }}
            >
              新用户首份礼包：存储空间从 56MB 点亮至 256MB
            </Typography>
          </Box>

          <Box
            sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 3 }}
          >
            {[
              "✨ 新注册用户专属，喜迎开通礼",
              `✨ 立即获赠 ${GIFT_SIZE_LABEL} 云端存储空间`,
              "✨ 礼包到账后可立即使用",
              "✨ 每个账号仅可领取一次",
            ].map((item) => (
              <Typography
                key={item}
                variant="body2"
                sx={{ color: "rgba(255, 248, 231, 0.88)", fontSize: "0.94rem" }}
              >
                {item}
              </Typography>
            ))}
          </Box>

          {message && (
            <Typography
              sx={{
                color: "rgba(255, 248, 231, 0.76)",
                mb: 2.5,
                textAlign: "center",
              }}
            >
              {message}
            </Typography>
          )}

          {claimError && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                borderRadius: 2.5,
                color: colors.ivory,
                background: "rgba(81, 8, 20, 0.72)",
                border: "1px solid rgba(255, 209, 102, 0.18)",
                "& .MuiAlert-icon": { color: colors.gold },
              }}
            >
              {claimError}
            </Alert>
          )}
        </Paper>

        <Typography
          variant="caption"
          display="block"
          textAlign="center"
          sx={{ color: "rgba(255, 248, 231, 0.68)", mt: 2.5 }}
        >
          活动最终解释权归知嘛团队所有
        </Typography>

        <Box
          sx={{
            position: "fixed",
            left: "50%",
            bottom: { xs: 16, sm: 24 },
            transform: "translateX(-50%)",
            width: "100%",
            maxWidth: 600,
            px: { xs: 3, sm: 4 },
            zIndex: 4,
            pointerEvents: "none",
          }}
        >
          <Box
            sx={{
              pointerEvents: "auto",
              background:
                "linear-gradient(180deg, rgba(217, 31, 63, 0) 0%, rgba(217, 31, 63, 0.3) 20%, rgba(179, 15, 42, 0.82) 100%)",
              borderRadius: 4,
              pt: 3,
              pb: 0.5,
            }}
          >
            <Button
              fullWidth
              variant="contained"
              size="large"
              disabled={claiming}
              onClick={handleClaim}
              startIcon={
                claiming ? (
                  <CircularProgress size={20} sx={{ color: colors.darkText }} />
                ) : (
                  <CardGiftcardIcon />
                )
              }
              sx={{
                ...gradientBtnSx,
                py: 1.8,
                fontSize: "1.08rem",
                borderRadius: 3,
              }}
            >
              {claiming ? "正在拆开礼包…" : "立即领取,下载优质资源"}
            </Button>
          </Box>
        </Box>
      </Container>
    </PageShell>
  );
}

function PageShell({ children }) {
  return (
    <Box sx={pageShellSx}>
      <FestiveEffects />
      <Lantern side="left" />
      <Lantern side="right" />
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
          textAlign: "center",
          position: "relative",
          zIndex: 2,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

function FestiveEffects() {
  return (
    <>
      <GlobalStyles
        styles={{
          "@keyframes lanternSwing": {
            "0%": { transform: "rotate(4deg) translateY(0px)" },
            "50%": { transform: "rotate(-4deg) translateY(5px)" },
            "100%": { transform: "rotate(4deg) translateY(0px)" },
          },
          "@keyframes festivePulse": {
            "0%": {
              boxShadow:
                "0 14px 30px rgba(86, 31, 2, 0.28), 0 0 0 rgba(255, 209, 102, 0.12)",
            },
            "50%": {
              boxShadow:
                "0 18px 36px rgba(86, 31, 2, 0.34), 0 0 30px rgba(255, 209, 102, 0.4)",
            },
            "100%": {
              boxShadow:
                "0 14px 30px rgba(86, 31, 2, 0.28), 0 0 0 rgba(255, 209, 102, 0.12)",
            },
          },
          "@keyframes fireworksBlink": {
            "0%": { opacity: 0.35, transform: "scale(0.95)" },
            "50%": { opacity: 0.8, transform: "scale(1.04)" },
            "100%": { opacity: 0.35, transform: "scale(0.95)" },
          },
          "@keyframes sparkleFloat": {
            "0%": { opacity: 0.25, transform: "translateY(0px) scale(0.96)" },
            "50%": { opacity: 0.75, transform: "translateY(-8px) scale(1.06)" },
            "100%": { opacity: 0.25, transform: "translateY(0px) scale(0.96)" },
          },
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          zIndex: 0,
        }}
      >
        {[
          { left: "10%", bottom: 40, size: 160, delay: "0s" },
          { left: "50%", bottom: 16, size: 210, delay: "0.8s" },
          { right: "10%", bottom: 54, size: 170, delay: "1.4s" },
        ].map((item) => (
          <Box
            key={JSON.stringify(item)}
            sx={{
              position: "absolute",
              ...item,
              width: item.size,
              height: item.size,
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(255, 209, 102, 0.28) 0%, rgba(255, 209, 102, 0.12) 32%, rgba(255, 209, 102, 0.04) 58%, transparent 74%)",
              filter: "blur(2px)",
              animation: `fireworksBlink 3.8s ease-in-out ${item.delay} infinite`,
            }}
          />
        ))}
        {[
          { left: "18%", top: "24%", delay: "0.2s" },
          { left: "32%", bottom: "22%", delay: "1.3s" },
          { right: "22%", top: "18%", delay: "0.9s" },
          { right: "28%", bottom: "20%", delay: "1.8s" },
        ].map((item, index) => (
          <Typography
            key={index}
            component="span"
            sx={{
              position: "absolute",
              ...item,
              color: colors.gold,
              fontSize: index % 2 === 0 ? "1.1rem" : "0.95rem",
              animation: `sparkleFloat 2.8s ease-in-out ${item.delay} infinite`,
              textShadow: "0 0 16px rgba(255, 209, 102, 0.55)",
            }}
          >
            ✨
          </Typography>
        ))}
      </Box>
    </>
  );
}

function Lantern({ side }) {
  const isLeft = side === "left";
  return (
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        top: { xs: 30, sm: 42 },
        [isLeft ? "left" : "right"]: { xs: 8, sm: 28 },
        width: { xs: 48, sm: 62 },
        zIndex: 1,
        display: { xs: "none", sm: "block" },
        transformOrigin: isLeft ? "top right" : "top left",
        animation: `lanternSwing ${isLeft ? "3.7s" : "4.1s"} ease-in-out infinite`,
      }}
    >
      <Box
        sx={{
          width: 2,
          height: 56,
          mx: "auto",
          background:
            "linear-gradient(180deg, rgba(255, 209, 102, 0.9), rgba(255, 209, 102, 0.18))",
        }}
      />
      <Box
        sx={{
          position: "relative",
          mx: "auto",
          mt: -1,
          width: "100%",
          height: { xs: 62, sm: 78 },
          borderRadius: "40% 40% 32% 32% / 24% 24% 40% 40%",
          background: `linear-gradient(180deg, #f25b33 0%, ${colors.red} 50%, #8f041c 100%)`,
          border: `2px solid ${colors.deepGold}`,
          boxShadow:
            "0 10px 28px rgba(76, 0, 6, 0.34), inset 0 0 18px rgba(255, 209, 102, 0.24)",
          "&::before, &::after": {
            content: '""',
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            width: "72%",
            borderTop: "1px solid rgba(255, 209, 102, 0.55)",
          },
          "&::before": { top: "32%" },
          "&::after": { top: "58%" },
        }}
      >
        <Typography
          sx={{ color: colors.gold, fontSize: "1.25rem", lineHeight: "78px" }}
        >
          🏮
        </Typography>
      </Box>
      <Box
        sx={{
          width: 10,
          height: 18,
          mx: "auto",
          mt: 0.2,
          borderRadius: "0 0 10px 10px",
          background: `linear-gradient(180deg, ${colors.deepGold}, #9b5f0f)`,
        }}
      />
      <Box
        sx={{
          width: 2,
          height: 22,
          mx: "auto",
          background: `linear-gradient(180deg, ${colors.deepGold}, rgba(255, 209, 102, 0))`,
        }}
      />
    </Box>
  );
}

function StatusCard({ icon, title, children }) {
  return (
    <Paper
      elevation={0}
      sx={{
        ...festiveCardSx,
        maxWidth: 420,
        width: "100%",
        p: { xs: 3, sm: 4 },
        textAlign: "center",
        zIndex: 2,
      }}
    >
      {icon}
      <Typography
        variant="h5"
        fontWeight={800}
        gutterBottom
        sx={{ color: colors.ivory }}
      >
        {title}
      </Typography>
      {children}
    </Paper>
  );
}

function StorageBlock({ label, value, color, bgColor, glow }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        background: bgColor,
        border: `1px solid ${color}55`,
        borderRadius: 3,
        px: { xs: 2.25, sm: 2.75 },
        py: 2,
        minWidth: { xs: 104, sm: 120 },
        boxShadow: glow ? `0 0 26px ${colors.goldGlow}` : "none",
      }}
    >
      <StorageIcon sx={{ color, fontSize: 28, mb: 0.5 }} />
      <Typography
        fontWeight={900}
        sx={{ color, fontSize: "1.35rem", lineHeight: 1 }}
      >
        {value}
      </Typography>
      <Typography
        variant="caption"
        sx={{ color: "rgba(255, 248, 231, 0.72)", mt: 0.5 }}
      >
        {label}
      </Typography>
    </Box>
  );
}

function SuccessCard({ claimedAt, message }) {
  const dateStr = claimedAt
    ? new Date(claimedAt).toLocaleString("zh-CN", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  return (
    <Paper
      elevation={0}
      sx={{
        ...festiveCardSx,
        maxWidth: 440,
        width: "100%",
        p: { xs: 3, sm: 4 },
        textAlign: "center",
        zIndex: 2,
      }}
    >
      <CheckCircleOutlineIcon
        sx={{ fontSize: 72, color: colors.gold, mb: 2 }}
      />
      <Typography
        variant="h5"
        fontWeight={800}
        gutterBottom
        sx={{ color: colors.ivory }}
      >
        已领取过，不可重复领取
      </Typography>
      <Typography sx={{ color: "rgba(255, 248, 231, 0.84)", mb: 1.2 }}>
        {message || `您已领取过该活动，已为您增加 ${GIFT_SIZE_LABEL} 存储空间`}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: colors.gold,
          fontWeight: 800,
          fontSize: "1.5rem",
          my: 2,
          textShadow: "0 0 18px rgba(255, 209, 102, 0.28)",
        }}
      >
        +{GIFT_SIZE_LABEL} 存储空间
      </Typography>
      {dateStr && (
        <Typography
          variant="caption"
          sx={{ color: "rgba(255, 248, 231, 0.7)" }}
        >
          领取时间：{dateStr}
        </Typography>
      )}
      <Typography
        variant="body2"
        sx={{ color: "rgba(255, 248, 231, 0.76)", mt: 2, fontSize: "0.86rem" }}
      >
        请返回知嘛小程序查看最新存储空间
      </Typography>
    </Paper>
  );
}

export default ActivityPage;
